// eslint-disable-next-line import/no-extraneous-dependencies
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GET_PARAMS } from "constants/popupConfig";
import {
  STORAGE_AUTH_TOKEN,
  STORAGE_CLICK_ID,
  STORAGE_CURRENCY_FROM_LINK,
  STORAGE_UTM_CAMPAIGN,
  STORAGE_UTM_MEDIUM,
  STORAGE_UTM_SOURCE,
} from "constants/variables";
import { useGTM } from "hooks/useGTM";
import AppRoutes from "AppRoutes";
import ErrorBoundary from "ErrorBoundary";
import RouteErrorBoundary from "RouterErrorBoundary";
import { useMatomoTM } from "hooks/useMatomoTM";
import useWebSocket from "hooks/useWebsocket";
import { useEffect } from "react";
import { SkeletonTheme } from "react-loading-skeleton";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { getLocalStorageItem, setLocalStorageItem } from "utils/localStorage";
// import AutologinIframe from "components/molecules/AutologinIframe";
import "@atlaskit/css-reset";
import "react-loading-skeleton/dist/skeleton.css";
import Toast from "components/molecules/Toast";
import "react-toastify/dist/ReactToastify.css";
import { useBloomreachInit } from "hooks/useBloomreachInit";

import "./theme/main.sass";
import { useUserLocation } from "./helpers/useUserLocation";

const { body } = document;

const queryClient = new QueryClient();

const router = createBrowserRouter([
  {
    path: "/*",
    element: <AppRoutes />,
    errorElement: <RouteErrorBoundary />,
  },
]);

const App = () => {
  const GTM = useGTM();
  const { VITE_APP_PROJECT_NAME } = import.meta.env;
  const { search } = window.location;
  const query = new URLSearchParams(search);
  const token = getLocalStorageItem(STORAGE_AUTH_TOKEN);
  useUserLocation();

  useEffect(() => {
    body.setAttribute("data-theme", "light");

    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      ) &&
      document.documentElement.requestFullscreen
    ) {
      document.documentElement.requestFullscreen();
    }
  }, []);

  useEffect(() => {
    if (VITE_APP_PROJECT_NAME) {
      body.setAttribute("data-project", VITE_APP_PROJECT_NAME);
    }
  }, [VITE_APP_PROJECT_NAME]);

  useEffect(() => {
    GTM.init();
    // useSocket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Init Matomo Tag Manager
  useMatomoTM();

  useWebSocket({ token });

  useBloomreachInit();

  useEffect(() => {
    const clickId = query.get(GET_PARAMS.CLICK_ID);
    const currency = query.get(GET_PARAMS.CURRENCY);
    const utmMedium = query.get(GET_PARAMS.UTM_MEDIUM);
    const utmSource = query.get(GET_PARAMS.UTM_SOURCE);
    const utmCampaign = query.get(GET_PARAMS.UTM_CAMPAIGN);
    if (currency) {
      setLocalStorageItem(STORAGE_CURRENCY_FROM_LINK, currency);
    }
    if (clickId) {
      setLocalStorageItem(STORAGE_CLICK_ID, clickId);
    }
    if (utmMedium) {
      setLocalStorageItem(STORAGE_UTM_MEDIUM, utmMedium);
    }
    if (utmSource) {
      setLocalStorageItem(STORAGE_UTM_SOURCE, utmSource);
    }
    if (utmCampaign) {
      setLocalStorageItem(STORAGE_UTM_CAMPAIGN, utmCampaign);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const baseColor = VITE_APP_PROJECT_NAME === "banger" ? "#292930" : "#CBD2ED";
  const highlightColor =
    VITE_APP_PROJECT_NAME === "banger" ? "#3F4250" : "#E6EBFE";
  // GENERATE_SOURCEMAP=false BUILD_PATH='./gloryBuild' VITE_APP_PROJECT_NAME='glory' react-scripts build && GENERATE_SOURCEMAP=false BUILD_PATH='./bangerBuild' VITE_APP_PROJECT_NAME='banger' react-scripts build
  return (
    <QueryClientProvider client={queryClient}>
      <SkeletonTheme
        baseColor={baseColor}
        highlightColor={highlightColor}
        duration={2}
        borderRadius={8}
      >
        <Toast />
        {/* <IntercomProvider appId={VITE_APP_INTERCOM_APP_ID || ''}> */}
        <ErrorBoundary>
          <RouterProvider router={router} />
        </ErrorBoundary>
        {/* </IntercomProvider> */}
        {/* <AutologinIframe /> */}
      </SkeletonTheme>
    </QueryClientProvider>
  );
};

export default App;
